import { useCurrentSite } from '@hotjar/site';
import { CardConfigIds } from '../SetupWizard/steps/InstallationMethod/types';

enum PERSONALISATION_PAGES {
  INSTALLATION_METHOD = 'installation-method',
}

const MAIN_CONFIG = {
  [PERSONALISATION_PAGES.INSTALLATION_METHOD]: {
    card1: {
      'org-small': undefined,
      'org-mid': CardConfigIds.SHARE,
      'org-large': CardConfigIds.SHARE,
      default: undefined,
    },
    card2: {
      'org-small': undefined,
      'org-mid': CardConfigIds.NPM,
      'org-large': CardConfigIds.NPM,
      default: undefined,
    },
  },
} as const;

const usePersonalisation = (page: PERSONALISATION_PAGES) => {
  return MAIN_CONFIG[page];
};

export type ORG_SIZES =
  | '1-10'
  | '11-50'
  | '51-100'
  | '101-250'
  | '251-1000'
  | '1000-5000'
  | '5000+';

export type ORG_SIZES_LABELS = 'org-small' | 'org-mid' | 'org-large' | 'default';

enum INSTALLATION_METHOD_ELEMENTS {
  CARD_1 = 'card1',
  CARD_2 = 'card2',
}

const GROUPED_ORG_SIZES = {
  '1-10': 'org-small',
  '11-50': 'org-small',
  '51-100': 'org-small',
  '101-250': 'org-mid',
  '251-1000': 'org-mid',
  '1000-5000': 'org-large',
  '5000+': 'org-large',
} as const;

export const getGroupedOrgSize = (size: ORG_SIZES): ORG_SIZES_LABELS => {
  return GROUPED_ORG_SIZES[size] || 'default';
};

const useOrgSize = (): ORG_SIZES_LABELS => {
  const { data: currentSite } = useCurrentSite();
  const orgSize = currentSite?.organization?.size;

  if (orgSize) {
    return getGroupedOrgSize(orgSize as ORG_SIZES);
  }

  return 'default';
};

type InstallationPersonlisationConfig = {
  [key in INSTALLATION_METHOD_ELEMENTS]: CardConfigIds | undefined;
};

export const useInstallationPersonalisation = (): InstallationPersonlisationConfig => {
  const orgSize = useOrgSize();
  const config = usePersonalisation(PERSONALISATION_PAGES.INSTALLATION_METHOD);

  return (Object.keys(config) as INSTALLATION_METHOD_ELEMENTS[]).reduce(
    (finalConfig, currentKey) => ({
      ...finalConfig,
      [currentKey]: orgSize && config[currentKey][orgSize],
    }),
    {} as InstallationPersonlisationConfig,
  );
};
